import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { HttpServiceBase } from '../classes/HttpServiceBase';
import { BackendService } from './backend.service';

/** One language's copy. Every field has a canonical English counterpart at the
 *  top level of AdminProject. */
export interface ProjectTranslation {
    title: string | null;
    description: string | null;
    detailedDescription: string | null;
    industry: string | null;
    location: string | null;
}

/**
 * A project as the admin editor sees it: both languages side by side.
 *
 * Deliberately not the public `Project` interface, which is already resolved to
 * one language — there, a missing Greek translation comes back as the English
 * text, so the editor could not tell an untranslated field from a translated one
 * that happens to match.
 */
export interface AdminProject {
    id: string;
    /** URL key for /projects/:slug. Null on a row not yet backfilled. */
    slug: string | null;
    title: string;
    /** Both optional in the editor, and null on rows migrated from the old
     *  site that never had them. */
    description: string | null;
    detailedDescription: string | null;
    industry: string | null;
    location: string | null;
    /**
     * Non-English copy, keyed by language code — whatever the row carries.
     *
     * A map rather than a fixed `el` field so adding a language means adding it
     * to LanguageService, with no change here or on the server.
     */
    translations: Record<string, ProjectTranslation>;
    year: string | null;
    displayOrder: number | null;
    /** Ready to render: signed when the photo was uploaded, the stored path
     *  otherwise. */
    image: string | null;
    hasUploadedImage: boolean;
    /** What is actually stored, so the editor can show a migrated row's path. */
    imageUrl: string | null;
}

/**
 * What the edit form submits.
 *
 * `image` is not sent — the server owns it. A new photo goes up as base64 and
 * the server stores the S3 key; omitting the image fields means "leave the
 * current photo alone" on an update, and is rejected on create, where a photo
 * is mandatory. There is no remove case: `imageUrl` is a required column and
 * the carousel has nothing to show without one.
 */
export interface ProjectPayload {
    slug: string;
    title: string;
    description: string;
    detailedDescription: string;
    industry: string | null;
    location: string | null;
    translations: Record<string, ProjectTranslation>;
    year: string | null;
    displayOrder: number | null;
    imageBase64?: string;
    imageFilename?: string;
    imageContentType?: string;
}

/**
 * Case-study CRUD for admins (backend rest/fyes/admin/projects).
 *
 * Reads for the public carousel go through BackendService.getProjects(); this is
 * the write side, and it is admin-only on the server (fyesAdminRequired).
 */
@Injectable({ providedIn: 'root' })
export class AdminProjectsService extends HttpServiceBase {
    protected override baseUrl = '/admin/projects';

    constructor(private http: HttpClient, private backend: BackendService) {
        super();
        this.assign(this);
    }

    /**
     * Drop the public carousel's cached copy after a write.
     *
     * That cache is held for an hour, so without this an admin saving a project
     * would keep seeing the old carousel on the public pages in the same tab and
     * reasonably conclude the save had not worked.
     */
    private invalidatePublicCache<T>(source: Observable<T>): Observable<T> {
        return source.pipe(tap(() => this.backend.invalidateProjects()));
    }

    public list(): Observable<any> {
        return this._http.get(this.baseUrl)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    public create(payload: ProjectPayload): Observable<any> {
        return this.invalidatePublicCache(this._http.post(this.baseUrl, payload)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this))));
    }

    public update(id: string, payload: ProjectPayload): Observable<any> {
        return this.invalidatePublicCache(this._http.put(`${this.baseUrl}/${id}`, payload)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this))));
    }

    /**
     * Save a dragged order: the full list of ids, in their new order.
     *
     * The server assigns 1..N by position, so the whole set has to be sent —
     * renumbering from 1 with only part of the list would collide with the rows
     * left out.
     */
    public reorder(ids: string[]): Observable<any> {
        return this.invalidatePublicCache(this._http.put(`${this.baseUrl}/reorder`, { ids })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this))));
    }

    public delete(id: string): Observable<any> {
        return this.invalidatePublicCache(this._http.delete(`${this.baseUrl}/${id}`)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this))));
    }
}
